import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Sparkles, Loader2, PlayCircle, Check, X, RefreshCw, Send, Bot, Activity,
  ThumbsUp, ThumbsDown, MessageCircle, Mail as MailIcon, StickyNote, Server,
  Pencil, Save, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const ACTION_ICON = {
  mark_task_done: Check,
  add_progress_note: StickyNote,
  restart_worker: Server,
  email_client: MailIcon,
  message_client: MessageCircle,
  flag_for_review: Sparkles,
  propose_task: Activity,
  draft_recovery_plan: ClipboardList,
};

const HEALTH_STYLES = {
  ok: "border-[#6b7c3a]/40 bg-[#6b7c3a]/10 text-[#4d5a24]",
  degraded: "border-[#c9a961]/40 bg-[#c9a961]/10 text-[#a3823f]",
  down: "border-[#b23a48]/40 bg-[#b23a48]/10 text-[#b23a48]",
  unknown: "border-[#1e3d3a]/12 bg-[#eae7de]/60 text-[#1e3d3a]/60",
};

export default function AiOps({ secret }) {
  const [reports, setReports] = useState(null);
  const [feedback, setFeedback] = useState({ totals: {}, recent: [] });
  const [scanning, setScanning] = useState(false);
  const [asking, setAsking] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [answer, setAnswer] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const beginEdit = (a) => {
    setEditingId(a.id);
    setEditDraft({
      subject: a.subject || "",
      body: a.body || "",
      note: a.note || "",
      reason: a.reason || "",
      plan: a.plan ? {
        problem_summary: a.plan.problem_summary || "",
        expected_monthly_savings: a.plan.expected_monthly_savings || 0,
        timeline_days: a.plan.timeline_days || 0,
        subject: a.plan.subject || "",
        body: a.plan.body || "",
      } : null,
    });
  };
  const cancelEdit = () => { setEditingId(null); setEditDraft({}); };
  const saveEdit = async (a) => {
    setSaving(true);
    const payload = {};
    if (editDraft.subject !== undefined && editDraft.subject !== a.subject) payload.subject = editDraft.subject;
    if (editDraft.body !== undefined && editDraft.body !== a.body) payload.body = editDraft.body;
    if (editDraft.note !== undefined && editDraft.note !== a.note) payload.note = editDraft.note;
    if (editDraft.reason !== undefined && editDraft.reason !== a.reason) payload.reason = editDraft.reason;
    if (editDraft.plan) payload.plan = editDraft.plan;
    try {
      await api.patch(`/admin/ai-ops/actions/${a.id}`, payload);
      toast.success("Changes saved.");
      cancelEdit();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const load = () => {
    api.get("/admin/ai-ops/reports")
      .then(({ data }) => setReports(data.reports || []))
      .catch(() => toast.error("Could not load AI Ops reports."));
    api.get("/admin/ai-ops/feedback")
      .then(({ data }) => setFeedback(data))
      .catch(() => { /* silent */ });
  };
  useEffect(() => { if (secret) load(); }, [secret]);

  const runScan = async () => {
    setScanning(true);
    try {
      await api.post("/admin/ai-ops/scan");
      toast.success("Scan complete.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed.");
    } finally {
      setScanning(false);
    }
  };

  const approve = async (actionId) => {
    try {
      await api.post(`/admin/ai-ops/actions/${actionId}/approve`);
      toast.success("Action approved.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed.");
    }
  };

  const dismiss = async (actionId) => {
    const note = window.prompt(
      "Optional: teach the AI why you're dismissing this (leave blank to skip).\n\n" +
      "Example: \"Don't send prep emails when the booking already has a stated goal.\""
    );
    try {
      await api.post(`/admin/ai-ops/actions/${actionId}/dismiss`, { notes: note || null });
      toast.success(note ? "Dismissed — AI will learn from this." : "Dismissed.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dismiss failed.");
    }
  };

  const ask = async () => {
    if (!prompt.trim()) return;
    setAsking(true);
    setAnswer(null);
    try {
      const { data } = await api.post("/admin/ai-ops/ask", { prompt });
      setAnswer(data.answer);
      load();
      toast.success(data.actions?.length
        ? `AI added ${data.actions.length} action(s) to the queue.`
        : "AI replied.");
      setPrompt("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI could not answer.");
    } finally {
      setAsking(false);
    }
  };

  const queuedAll = (reports || []).flatMap((r) =>
    (r.queued || []).map((a) => ({ ...a, report_id: r.id, created_at: r.created_at }))
  ).filter((a) => a && a.id && a.type);
  const latest = reports?.[0];
  const health = latest?.site_health || { status: "unknown", summary: "Run a scan to check.", flags: [] };

  return (
    <div data-testid="tab-content-ai-ops" className="space-y-5">
      {/* Toolbar + Learning tally */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#1e3d3a]/12 bg-white px-5 py-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#1e3d3a]/55">
            <Bot className="h-3.5 w-3.5 text-[#c9a961]" />
            AI Ops · learning
          </div>
          <span data-testid="ai-ops-approved-count" className="text-[11px] text-[#1e3d3a]/60">
            <span className="text-[#6b7c3a] font-semibold">{feedback.totals?.approved || 0}</span> approved
          </span>
          <span data-testid="ai-ops-applied-count" className="text-[11px] text-[#1e3d3a]/60">
            <span className="text-[#c9a961] font-semibold">{feedback.totals?.applied || 0}</span> auto-applied
          </span>
          <span data-testid="ai-ops-dismissed-count" className="text-[11px] text-[#1e3d3a]/60">
            <span className="text-[#b23a48] font-semibold">{feedback.totals?.dismissed || 0}</span> dismissed
          </span>
          {feedback.weekly?.delta_pct !== null && feedback.weekly?.delta_pct !== undefined && (
            <span
              data-testid="ai-ops-weekly-delta"
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-[0.18em] font-semibold ${
                feedback.weekly.delta_pct > 0
                  ? "bg-[#6b7c3a]/12 text-[#4d5a24] border border-[#6b7c3a]/30"
                  : feedback.weekly.delta_pct < 0
                  ? "bg-[#b23a48]/8 text-[#b23a48] border border-[#b23a48]/30"
                  : "bg-[#1e3d3a]/6 text-[#1e3d3a]/60 border border-[#1e3d3a]/12"
              }`}
              title={`This week: ${feedback.weekly.this_week_rate ?? 0}% approval (${feedback.weekly.this_week_approved}/${feedback.weekly.this_week_total}). Last week: ${feedback.weekly.last_week_rate ?? "—"}%.`}
            >
              {feedback.weekly.delta_pct > 0 ? "▲" : feedback.weekly.delta_pct < 0 ? "▼" : "•"}
              {Math.abs(feedback.weekly.delta_pct)}% this week
            </span>
          )}
          {feedback.weekly?.delta_pct === null && (feedback.weekly?.this_week_total || 0) > 0 && (
            <span
              data-testid="ai-ops-weekly-delta"
              className="inline-flex items-center gap-1 rounded-full bg-[#c9a961]/12 text-[#a3823f] border border-[#c9a961]/30 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.18em] font-semibold"
            >
              First week · {feedback.weekly.this_week_rate ?? 0}% approval
            </span>
          )}
        </div>
        <button
          type="button"
          data-testid="ai-ops-scan"
          onClick={runScan}
          disabled={scanning}
          className="inline-flex items-center gap-2 rounded-full bg-[#1e3d3a] text-white px-4 py-1.5 text-xs uppercase tracking-[0.2em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-60"
        >
          {scanning ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Scanning…</> : <><PlayCircle className="h-3.5 w-3.5" /> Run scan</>}
        </button>
      </div>

      {/* Ask the AI */}
      <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
        <div className="text-[10px] uppercase tracking-[0.24em] text-[#c9a961] mb-2">Ask the copilot</div>
        <p className="text-xs text-[#1e3d3a]/55 mb-3">
          Ask a question or request an action — the AI will reply and add proposed actions to the queue.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            data-testid="ai-ops-prompt"
            className="flex-1 rounded-xl border border-[#1e3d3a]/15 bg-white px-4 py-2.5 text-sm placeholder:text-[#1e3d3a]/35 focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25 transition-colors"
            placeholder='e.g. "Draft a check-in email for the Ridgeway team"'
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") ask(); }}
          />
          <button
            data-testid="ai-ops-ask"
            type="button"
            onClick={ask}
            disabled={asking || !prompt.trim()}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1e3d3a] text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-60"
          >
            {asking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5" /> Ask</>}
          </button>
        </div>
        {answer && (
          <div data-testid="ai-ops-answer" className="mt-3 rounded-lg border border-[#c9a961]/40 bg-[#c9a961]/8 px-4 py-3 text-sm text-[#1e3d3a]">
            {answer}
          </div>
        )}
      </div>

      {/* Site health */}
      <motion.div
        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
        className={`rounded-2xl border p-5 ${HEALTH_STYLES[health.status] || HEALTH_STYLES.unknown}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] opacity-70">Site health</div>
            <div className="font-head text-2xl mt-1 capitalize">{health.status || "unknown"}</div>
            {health.summary && <p className="mt-2 text-sm max-w-2xl">{health.summary}</p>}
          </div>
          {latest?.created_at && (
            <span className="text-[10px] opacity-60 uppercase tracking-[0.2em]">
              Last: {new Date(latest.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
            </span>
          )}
        </div>
        {(health.flags || []).length > 0 && (
          <ul className="mt-3 space-y-1 text-xs">
            {health.flags.map((f, i) => <li key={i}>· {f}</li>)}
          </ul>
        )}
      </motion.div>

      {/* Queue */}
      <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#c9a961]">Queued actions · needs review</div>
          <button
            type="button"
            onClick={load}
            className="text-[#1e3d3a]/55 hover:text-[#a3823f] transition-colors duration-300"
            title="Refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
        {reports === null && <p className="text-xs text-[#1e3d3a]/55">Loading…</p>}
        {reports !== null && queuedAll.length === 0 && (
          <p data-testid="ai-ops-empty" className="text-xs text-[#1e3d3a]/55">Nothing queued. Run a scan or ask the copilot for a task.</p>
        )}
        <ul className="divide-y divide-[#1e3d3a]/8">
          {queuedAll.map((a) => {
            const Icon = ACTION_ICON[a.type] || Sparkles;
            const risky = ["email_client", "message_client"].includes(a.type);
            return (
              <li key={a.id} data-testid={`ai-ops-action-${a.id}`} className="py-3">
                <div className="flex items-start gap-3">
                  <span className={`shrink-0 mt-0.5 h-8 w-8 rounded-full grid place-items-center ${risky ? "bg-[#c9a961]/12 text-[#a3823f]" : "bg-[#1e3d3a]/6 text-[#1e3d3a]"}`}>
                    <Icon className="h-4 w-4" strokeWidth={1.7} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="text-sm font-medium text-[#1e3d3a]">{(a.type || "action").replace(/_/g, " ")}</span>
                      {a.confidence && (
                        <span className={`text-[9px] uppercase tracking-[0.2em] ${a.confidence === "high" ? "text-[#6b7c3a]" : a.confidence === "medium" ? "text-[#a3823f]" : "text-[#1e3d3a]/45"}`}>
                          · {a.confidence} confidence
                        </span>
                      )}
                      {a.client_id && (
                        <span className="text-[10px] text-[#1e3d3a]/55 truncate">· {a.client_id}</span>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-[#1e3d3a]/65">{a.reason}</p>
                    {a.subject && editingId !== a.id && (
                      <div className="mt-2 rounded-lg border border-[#1e3d3a]/12 bg-[#eae7de]/40 px-3 py-2 text-xs text-[#1e3d3a]">
                        <div className="font-semibold">{a.subject}</div>
                        {a.body && <div className="mt-1 whitespace-pre-wrap text-[#1e3d3a]/70">{a.body}</div>}
                        {a.edited_at && <div className="mt-1 text-[10px] italic text-[#a3823f]">Edited</div>}
                      </div>
                    )}
                    {a.plan && editingId !== a.id && (
                      <div className="mt-2 rounded-lg border border-[#c9a961]/40 bg-[#c9a961]/6 px-3 py-2 text-xs text-[#1e3d3a] space-y-1.5">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="font-semibold">Recovery plan draft</span>
                          {a.plan.expected_monthly_savings > 0 && (
                            <span className="text-[10px] text-[#a3823f] uppercase tracking-[0.18em]">
                              ${Math.round(a.plan.expected_monthly_savings).toLocaleString()}/mo · {a.plan.timeline_days || "?"}d
                            </span>
                          )}
                        </div>
                        {a.plan.problem_summary && <p className="text-[#1e3d3a]/75">{a.plan.problem_summary}</p>}
                        {a.plan.phases?.length > 0 && (
                          <ul className="ml-4 list-disc space-y-0.5 text-[#1e3d3a]/70">
                            {a.plan.phases.map((p, i) => (
                              <li key={i}><strong>Phase {p.phase || i + 1}:</strong> {p.focus}</li>
                            ))}
                          </ul>
                        )}
                        {a.edited_at && <div className="text-[10px] italic text-[#a3823f]">Edited</div>}
                      </div>
                    )}
                    {editingId === a.id && (
                      <div data-testid={`ai-ops-edit-${a.id}`} className="mt-2 rounded-lg border border-[#c9a961]/60 bg-[#c9a961]/5 px-3 py-3 space-y-2">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Edit before approve</div>
                        {(a.type === "email_client" || a.type === "message_client") && (
                          <>
                            <input
                              data-testid={`ai-ops-edit-subject-${a.id}`}
                              value={editDraft.subject || ""}
                              onChange={(e) => setEditDraft((d) => ({ ...d, subject: e.target.value }))}
                              placeholder="Subject"
                              className="w-full text-xs rounded border border-[#1e3d3a]/15 bg-white px-2 py-1.5 focus:border-[#c9a961] focus:outline-none"
                            />
                            <textarea
                              data-testid={`ai-ops-edit-body-${a.id}`}
                              value={editDraft.body || ""}
                              onChange={(e) => setEditDraft((d) => ({ ...d, body: e.target.value }))}
                              placeholder="Body"
                              rows={5}
                              className="w-full text-xs rounded border border-[#1e3d3a]/15 bg-white px-2 py-1.5 focus:border-[#c9a961] focus:outline-none"
                            />
                          </>
                        )}
                        {a.type === "propose_task" && (
                          <input
                            data-testid={`ai-ops-edit-note-${a.id}`}
                            value={editDraft.note || ""}
                            onChange={(e) => setEditDraft((d) => ({ ...d, note: e.target.value }))}
                            placeholder="Task label"
                            className="w-full text-xs rounded border border-[#1e3d3a]/15 bg-white px-2 py-1.5 focus:border-[#c9a961] focus:outline-none"
                          />
                        )}
                        {a.type === "add_progress_note" && (
                          <textarea
                            data-testid={`ai-ops-edit-note-${a.id}`}
                            value={editDraft.note || ""}
                            onChange={(e) => setEditDraft((d) => ({ ...d, note: e.target.value }))}
                            placeholder="Note text"
                            rows={3}
                            className="w-full text-xs rounded border border-[#1e3d3a]/15 bg-white px-2 py-1.5 focus:border-[#c9a961] focus:outline-none"
                          />
                        )}
                        {editDraft.plan && (
                          <>
                            <textarea
                              data-testid={`ai-ops-edit-plan-problem-${a.id}`}
                              value={editDraft.plan.problem_summary || ""}
                              onChange={(e) => setEditDraft((d) => ({ ...d, plan: { ...d.plan, problem_summary: e.target.value } }))}
                              placeholder="Problem summary"
                              rows={3}
                              className="w-full text-xs rounded border border-[#1e3d3a]/15 bg-white px-2 py-1.5 focus:border-[#c9a961] focus:outline-none"
                            />
                            <div className="grid grid-cols-2 gap-2">
                              <input
                                data-testid={`ai-ops-edit-plan-savings-${a.id}`}
                                type="number"
                                value={editDraft.plan.expected_monthly_savings || 0}
                                onChange={(e) => setEditDraft((d) => ({ ...d, plan: { ...d.plan, expected_monthly_savings: Number(e.target.value) } }))}
                                placeholder="$/month savings"
                                className="text-xs rounded border border-[#1e3d3a]/15 bg-white px-2 py-1.5 focus:border-[#c9a961] focus:outline-none"
                              />
                              <input
                                data-testid={`ai-ops-edit-plan-timeline-${a.id}`}
                                type="number"
                                value={editDraft.plan.timeline_days || 0}
                                onChange={(e) => setEditDraft((d) => ({ ...d, plan: { ...d.plan, timeline_days: Number(e.target.value) } }))}
                                placeholder="Timeline (days)"
                                className="text-xs rounded border border-[#1e3d3a]/15 bg-white px-2 py-1.5 focus:border-[#c9a961] focus:outline-none"
                              />
                            </div>
                            <input
                              data-testid={`ai-ops-edit-plan-subject-${a.id}`}
                              value={editDraft.plan.subject || ""}
                              onChange={(e) => setEditDraft((d) => ({ ...d, plan: { ...d.plan, subject: e.target.value } }))}
                              placeholder="Delivery email subject"
                              className="w-full text-xs rounded border border-[#1e3d3a]/15 bg-white px-2 py-1.5 focus:border-[#c9a961] focus:outline-none"
                            />
                            <textarea
                              data-testid={`ai-ops-edit-plan-body-${a.id}`}
                              value={editDraft.plan.body || ""}
                              onChange={(e) => setEditDraft((d) => ({ ...d, plan: { ...d.plan, body: e.target.value } }))}
                              placeholder="Delivery email cover message"
                              rows={3}
                              className="w-full text-xs rounded border border-[#1e3d3a]/15 bg-white px-2 py-1.5 focus:border-[#c9a961] focus:outline-none"
                            />
                          </>
                        )}
                        <div className="flex items-center gap-2 pt-1">
                          <button
                            type="button"
                            data-testid={`ai-ops-edit-save-${a.id}`}
                            onClick={() => saveEdit(a)}
                            disabled={saving}
                            className="inline-flex items-center gap-1 rounded-full bg-[#1e3d3a] text-white px-3 py-1 text-[10px] uppercase tracking-[0.18em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
                          >
                            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
                          </button>
                          <button
                            type="button"
                            data-testid={`ai-ops-edit-cancel-${a.id}`}
                            onClick={cancelEdit}
                            className="inline-flex items-center gap-1 rounded-full border border-[#1e3d3a]/15 text-[#1e3d3a]/55 px-3 py-1 text-[10px] uppercase tracking-[0.18em] hover:text-[#b23a48] transition-colors duration-300"
                          >
                            <X className="h-3 w-3" /> Cancel
                          </button>
                        </div>
                      </div>
                    )}
                    {a.note && (
                      <div className="mt-2 rounded-lg border border-[#1e3d3a]/12 bg-[#eae7de]/40 px-3 py-2 text-xs italic text-[#1e3d3a]/70">
                        &ldquo;{a.note}&rdquo;
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 flex items-center gap-1">
                    {editingId !== a.id && (
                      <button
                        type="button"
                        data-testid={`ai-ops-edit-${a.id}-btn`}
                        onClick={() => beginEdit(a)}
                        title="Edit before approving"
                        className="inline-flex items-center gap-1 rounded-full border border-[#1e3d3a]/15 text-[#1e3d3a]/55 px-3 py-1 text-[10px] uppercase tracking-[0.18em] hover:text-[#a3823f] hover:border-[#c9a961] transition-colors duration-300"
                      >
                        <Pencil className="h-3 w-3" /> Edit
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid={`ai-ops-approve-${a.id}`}
                      onClick={() => approve(a.id)}
                      title="Approve + apply"
                      className="inline-flex items-center gap-1 rounded-full border border-[#6b7c3a]/50 bg-[#6b7c3a]/8 text-[#4d5a24] px-3 py-1 text-[10px] uppercase tracking-[0.18em] hover:bg-[#6b7c3a]/16 transition-colors duration-300"
                    >
                      <ThumbsUp className="h-3 w-3" /> Approve
                    </button>
                    <button
                      type="button"
                      data-testid={`ai-ops-dismiss-${a.id}`}
                      onClick={() => dismiss(a.id)}
                      title="Dismiss + optionally teach the AI why"
                      className="inline-flex items-center gap-1 rounded-full border border-[#1e3d3a]/15 text-[#1e3d3a]/55 px-3 py-1 text-[10px] uppercase tracking-[0.18em] hover:text-[#b23a48] hover:border-[#b23a48]/40 transition-colors duration-300"
                    >
                      <ThumbsDown className="h-3 w-3" /> Dismiss
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Recent feedback trail (proof the AI is learning) */}
      {(feedback.recent || []).length > 0 && (
        <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#c9a961] mb-3">What the AI has learned</div>
          <ul className="space-y-2">
            {feedback.recent.slice(0, 8).map((f) => (
              <li key={f.id} className="flex items-start gap-2 text-xs text-[#1e3d3a]/70">
                {f.outcome === "approved" || f.outcome === "applied"
                  ? <ThumbsUp className="h-3 w-3 mt-0.5 text-[#6b7c3a] shrink-0" />
                  : <ThumbsDown className="h-3 w-3 mt-0.5 text-[#b23a48] shrink-0" />}
                <div className="min-w-0">
                  <span className="text-[#1e3d3a] font-medium capitalize">{(f.action_type || "").replace(/_/g, " ")}</span>
                  <span className="text-[#1e3d3a]/45"> · {f.outcome}</span>
                  {f.owner_notes && (
                    <p className="mt-0.5 text-[11px] italic text-[#1e3d3a]/55">&ldquo;{f.owner_notes}&rdquo;</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
